import React, { useEffect, useState } from 'react';
import { isCompleted, isValidEmail, validate } from 'utils/Validation';
import CustomDialog from 'components/CustomDialog';
import { clearFeedback, sendFeedback } from 'store/slices/feedback';
import Grid from '@mui/material/Unstable_Grid2';
import { Button, TextField, Typography } from '@mui/material';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import ErrorMessage from '../ErrorMessage';
import Box from '@mui/material/Box';
import ReCAPTCHA from 'react-google-recaptcha';

const ContactUs = () => {
    const dispatch = useAppDispatch();
    const auth = useAppSelector((state) => state.auth);
    const feedback = useAppSelector((state) => state.feedback);
    const { sendingFeedback, feedbackSendSuccess, feedbackSendError } = feedback;
    const { tokenData } = auth;
    const privateModeOn = !!(tokenData && tokenData.data);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [showErrors, setShowErrors] = useState(false);

    const [errors, setErrors] = useState({
        name: false,
        email: false,
        message: false,
        captcha: false,
    });

    useEffect(() => {
        dispatch(clearFeedback());
    }, []);

    const validators = [
        {
            fieldName: 'name',
            fieldValue: name,
            isValid: (v: string) => privateModeOn || isCompleted(v),
        },
        {
            fieldName: 'email',
            fieldValue: email,
            isValid: (v: string) => privateModeOn || isValidEmail(v),
        },
        {
            fieldName: 'message',
            fieldValue: message,
            isValid: isCompleted,
        },
        {
            fieldName: 'captcha',
            fieldValue: captchaToken,
            isValid: (v: string | null) => !!v,
        },
    ];

    const handleSendClick = () => {
        const isValid = validate(validators, setErrors);
        if (isValid) {
            void dispatch(sendFeedback({ name, email, message, captchaToken }));
        } else {
            setShowErrors(true);
        }
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
        validate(validators, setErrors);
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
        validate(validators, setErrors);
    };

    const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMessage(event.target.value);
        validate(validators, setErrors);
    };

    return (
        <CustomDialog
            processing={sendingFeedback}
            processingCopy={'Sending your feedback...'}
            error={feedbackSendError}
            errorContent={
                <ErrorMessage
                    specificError={'We couldn\'t send your feedback at this time. Please try again later.'}
                />
            }
            success={feedbackSendSuccess}
            successContent={
                <>
                    <Typography variant={'subtitle1'} textAlign={'center'}>
                        Thanks for reaching out! 🙌
                    </Typography>
                    <Typography variant={'body1'} textAlign={'center'}>
                        We appreciate your feedback, and the KwadHub team will be in touch with you soon.
                    </Typography>
                </>
            }
            title={'Contact Us'}
            alternative={
                !feedbackSendSuccess && !feedbackSendError
                    ? 'We\'d love to hear from you! Share your thoughts on how we can make KwadHub better.'
                    : null
            }
        >
            <Grid container spacing={1}>
                {!privateModeOn && (
                    <>
                        <Grid xs={12}>
                            <TextField
                                label='Your Name'
                                type='text'
                                value={name}
                                onChange={handleNameChange}
                                placeholder='Name'
                                error={showErrors && errors.name}
                                fullWidth
                            />
                        </Grid>
                        {showErrors && errors.name && (
                            <Grid xs={12}>
                                <Box color='error.main' textAlign='center' mt={1}>
                                    Please enter your name.
                                </Box>
                            </Grid>
                        )}
                        <Grid xs={12}>
                            <TextField
                                id='email'
                                type='email'
                                value={email}
                                onChange={handleEmailChange}
                                label='Email Address'
                                placeholder='Enter your email'
                                error={showErrors && errors.email}
                                fullWidth
                            />
                        </Grid>
                        {showErrors && errors.email && (
                            <Grid xs={12}>
                                <Box color='error.main' textAlign='center' mt={1}>
                                    That doesn't look like a valid email address.
                                </Box>
                            </Grid>
                        )}
                    </>
                )}
                <Grid xs={12}>
                    <TextField
                        error={showErrors && errors.message}
                        label='Your Message'
                        multiline
                        rows={6}
                        value={message}
                        onChange={handleMessageChange}
                        variant='outlined'
                        fullWidth
                    />
                </Grid>
                {showErrors && errors.message && (
                    <Grid xs={12}>
                        <Box color='error.main' textAlign='center' mt={1}>
                            Please enter a message for us.
                        </Box>
                    </Grid>
                )}
                <Grid xs={12}>
                    <Box mt={2}>
                        {process.env.REACT_APP_CAPTCHA_SITE_KEY ? (
                            <ReCAPTCHA
                                sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                                onChange={(token: string) => setCaptchaToken(token)}
                            />
                        ) : (
                            <Typography color="error" variant="caption">
                                Missing recaptcha key.
                            </Typography>
                        )}
                        {showErrors && !captchaToken && (
                            <Typography variant="caption" color="error">
                                Please verify you're human.
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <Box
                sx={{
                    width: '100%',
                    textAlign: 'center',
                    pt: 2,
                }}
            >
                <Button variant='contained' onClick={handleSendClick}>
                    Send Feedback
                </Button>
            </Box>
        </CustomDialog>
    );
};

export default ContactUs;
